import React, { useEffect, useState } from 'react';
import BottomSheet from '../components/BottomSheet';
import { validateEmail } from '../utils/profile';

export default function EditProfileSheet({ open, user, onClose, onSave }) {
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [telephone, setTelephone] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [pwdCurrent, setPwdCurrent] = useState('');
  const [pwdNew, setPwdNew] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPrenom(user?.prenom || '');
    setNom(user?.nom || '');
    setTelephone(user?.telephone || '');
    setShowPwd(false);
    setPwdCurrent(''); setPwdNew(''); setPwdConfirm('');
    setErrors({}); setToast(null);
  }, [open, user]);

  const close = () => { if (!saving) onClose && onClose(); };

  const validate = () => {
    const e = {};
    if (!prenom.trim()) e.prenom = 'Prénom requis';
    if (showPwd) {
      if (!pwdCurrent) e.pwdCurrent = 'Mot de passe actuel requis';
      if (!pwdNew || pwdNew.length < 8) e.pwdNew = 'Au moins 8 caractères';
      if (pwdNew !== pwdConfirm) e.pwdConfirm = 'Les mots de passe ne correspondent pas';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setToast(null);
    try {
      const payload = {
        prenom: prenom.trim(),
        nom: nom.trim(),
        telephone: telephone.trim(),
      };
      if (showPwd) {
        payload.mot_de_passe_actuel = pwdCurrent;
        payload.nouveau_mot_de_passe = pwdNew;
      }
      await onSave(payload);
      setToast('✅ Profil mis à jour');
      setTimeout(() => { close(); }, 1000);
    } catch (e) {
      setToast('❌ ' + (e.message || 'Erreur de mise à jour'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <BottomSheet open={open} onClose={close} title="Modifier mon profil" height="full">
      <Section title="Identité">
        <Field label="Prénom" value={prenom} onChange={setPrenom} error={errors.prenom} disabled={saving} />
        <Field label="Nom" value={nom} onChange={setNom} disabled={saving} />
      </Section>

      <Section title="Contact">
        <Field
          label="Email"
          value={user?.email || ''}
          readonly
          helper="Pour modifier l'email, contactez le support à florian@getrevyo.com"
          isValid={validateEmail(user?.email)}
        />
        <Field label="Téléphone" type="tel" value={telephone} onChange={setTelephone} disabled={saving} />
      </Section>

      <Section title="Sécurité">
        {!showPwd ? (
          <button type="button" onClick={() => setShowPwd(true)} style={styles.linkBtn}>
            Modifier mon mot de passe
          </button>
        ) : (
          <>
            <Field label="Mot de passe actuel" type="password" value={pwdCurrent} onChange={setPwdCurrent} error={errors.pwdCurrent} disabled={saving} />
            <Field label="Nouveau mot de passe" type="password" value={pwdNew} onChange={setPwdNew} error={errors.pwdNew} disabled={saving} helper="8 caractères minimum" />
            <Field label="Confirmation" type="password" value={pwdConfirm} onChange={setPwdConfirm} error={errors.pwdConfirm} disabled={saving} />
            <button type="button" onClick={() => setShowPwd(false)} style={styles.linkBtnSm}>Annuler</button>
          </>
        )}
      </Section>

      <button type="button" onClick={handleSave} disabled={saving} style={{ ...styles.cta, opacity: saving ? 0.7 : 1 }}>
        {saving ? 'Enregistrement…' : 'Enregistrer'}
      </button>

      {toast && <div style={styles.toast}>{toast}</div>}
    </BottomSheet>
  );
}

function Section({ title, children }) {
  return (
    <section style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      <div style={styles.sectionBody}>{children}</div>
    </section>
  );
}

function Field({ label, value, onChange, type = 'text', error, helper, disabled, readonly, isValid }) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={readonly ? undefined : (e) => onChange(e.target.value)}
        disabled={disabled || readonly}
        readOnly={readonly}
        style={{
          ...styles.input,
          ...(error ? styles.inputError : null),
          ...(readonly ? styles.inputReadonly : null),
        }}
      />
      {error && <span style={styles.errorMsg}>{error}</span>}
      {helper && !error && <span style={styles.helper}>{helper}{isValid === false ? ' (format invalide)' : ''}</span>}
    </div>
  );
}

const styles = {
  section: { marginBottom: 18 },
  sectionTitle: {
    margin: '0 0 8px',
    fontSize: 11,
    fontWeight: 700,
    color: '#6B6B6B',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  sectionBody: { display: 'flex', flexDirection: 'column', gap: 10 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 12, fontWeight: 600, color: '#6B6B6B' },
  input: {
    border: '1px solid #E5E5E0',
    background: '#FFFFFF',
    borderRadius: 10,
    padding: '11px 14px',
    fontSize: 15,
    fontFamily: 'inherit',
    color: '#1A1A1A',
    outline: 'none',
  },
  inputError: { borderColor: '#C8442A' },
  inputReadonly: { background: '#FAF8F4', color: '#6B6B6B' },
  errorMsg: { color: '#C8442A', fontSize: 12, marginTop: 2 },
  helper: { color: '#9A9A9A', fontSize: 11 },
  linkBtn: {
    background: 'transparent',
    border: '1px dashed #E5E5E0',
    borderRadius: 10,
    padding: 12,
    color: '#E8502A',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 14,
  },
  linkBtnSm: {
    background: 'transparent',
    border: 'none',
    color: '#6B6B6B',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 12,
    padding: 4,
    alignSelf: 'flex-start',
  },
  cta: {
    width: '100%',
    background: '#E8502A',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 12,
    padding: '14px 16px',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    marginTop: 8,
  },
  toast: {
    marginTop: 12,
    padding: '10px 14px',
    background: '#E8F5EE',
    color: '#2D7A4A',
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 10,
    textAlign: 'center',
  },
};
